<template>
  <!-- Shared KR update-timeline list (contract C6). Rendered inside both the
       row's history popover (capped at 10 + "Show all") and the KR detail
       dialog (uncapped, scrolling). One row vocabulary: actor mark (avatar or
       automation zap tile), head line, value line (with status chip),
       prominent note. -->
  <div class="krtl">
    <div v-if="items.length === 0" class="krtl__empty">
      {{ $t('performance.goals.kr_history_empty') }}
    </div>

    <ul v-else class="krtl__list" :style="{ maxHeight: listMaxHeight > 0 ? listMaxHeight + 'px' : 'none' }">
      <li
        v-for="(item, idx) in visibleItems"
        :key="idx"
        class="krtl-row"
      >
        <!-- Actor mark: human avatar, or the zap tile for automation.
             'system:automation' is never rendered as a person (contract C5). -->
        <div class="krtl-row__mark">
          <span v-if="item.isAutomation" class="krtl-row__zap">
            <happily-icon name="zap" :size="12" />
          </span>
          <span
            v-else
            class="krtl-row__avatar"
            :style="avatarStyle(item)"
          >
            <template v-if="!avatarPic(item)">{{ actorInitials(item) }}</template>
          </span>
        </div>

        <div class="krtl-row__main">
          <div class="krtl-row__head">
            <span class="krtl-row__actor" :class="{ 'krtl-row__actor--automation': item.isAutomation }">
              {{ actorName(item) }}
            </span>
            <span class="krtl-row__time">{{ relTime(item.updated_at) }}</span>
          </div>
          <!-- Individual mode leads with the person's own number (that's what
               they updated); the KR-level rollup is a labeled tag so it can't
               be mistaken for a personal value. Legacy entries without a
               recorded slice show only the labeled rollup. -->
          <div v-if="isIndividual" class="krtl-row__value-line">
            <template v-if="item.ownerValue !== null">
              <span class="krtl-row__value">
                {{ formatValue(item.ownerValue) }}<template v-if="ownerTarget(item)"> / {{ formatValue(ownerTarget(item)) }}</template>
              </span>
              <span
                v-if="item.ownerDelta !== null && item.ownerDelta !== 0"
                class="krtl-row__delta"
                :class="item.ownerDelta > 0 ? 'krtl-row__delta--up' : 'krtl-row__delta--down'"
              >{{ formatDelta(item.ownerDelta) }}</span>
            </template>
            <span class="krtl-row__team-total" :class="{ 'krtl-row__team-total--lead': item.ownerValue === null }">
              {{ $t('performance.goals.kr_history_team_total', { value: formatValue(item.value) }) }}
            </span>
            <span
              v-if="item.status"
              class="krtl-row__status"
              :style="statusChipStyle(item.status)"
            >{{ statusLabel(item.status) }}</span>
          </div>
          <div v-else class="krtl-row__value-line">
            <span class="krtl-row__value">{{ formatValue(item.value) }}</span>
            <span
              v-if="item.delta !== null && item.delta !== 0"
              class="krtl-row__delta"
              :class="item.delta > 0 ? 'krtl-row__delta--up' : 'krtl-row__delta--down'"
            >{{ formatDelta(item.delta) }}</span>
            <span
              v-if="item.status"
              class="krtl-row__status"
              :style="statusChipStyle(item.status)"
            >{{ statusLabel(item.status) }}</span>
          </div>
          <div v-if="item.note" class="krtl-row__note">{{ item.note }}</div>
        </div>
      </li>
    </ul>

    <button
      v-if="hiddenCount > 0"
      type="button"
      class="krtl__show-all"
      @click="showAll = true"
    >
      {{ $t('performance.goals.kr_history_show_all', { count: items.length }) }}
    </button>
  </div>
</template>

<script>
import { KR_STATUS_COLORS, KR_STATUS_LABELS } from '@/constants/krStatus';
import {
  buildKrTimeline,
  formatKrValue,
  formatRelativeKrTime,
  toolkitDisplayName,
} from '@/store/utils/krUpdateRules';
import { computeOwnerTargetMap, formatTargetNumber } from '@/store/goals';

export default {
  name: 'KrTimelineList',
  props: {
    keyResult: { type: Object, required: true },
    // Max rows before the "Show all" button appears. 0 = show everything.
    cap: { type: Number, default: 0 },
    // Scroll height of the list body. 0 = no internal max-height (let the
    // parent container own the scrolling).
    listMaxHeight: { type: Number, default: 340 },
  },
  data() {
    return {
      showAll: false,
    };
  },
  computed: {
    items() {
      // Passing the KR lets individual-mode rows reconstruct each person's
      // slice for legacy entries, so their own number can lead on every row.
      return buildKrTimeline(this.keyResult.progress_history, this.keyResult);
    },
    visibleItems() {
      if (this.cap <= 0 || this.showAll) return this.items;
      return this.items.slice(0, this.cap);
    },
    hiddenCount() {
      return Math.max(this.items.length - this.visibleItems.length, 0);
    },
    unit() {
      return this.keyResult.unit || '';
    },
    isIndividual() {
      return this.keyResult.mode === 'individual';
    },
    ownerTargetMap() {
      if (!this.isIndividual) return {};
      return computeOwnerTargetMap(
        this.keyResult.owner_emails,
        this.keyResult.target_value,
        this.keyResult.owner_targets || {},
      );
    },
  },
  methods: {
    // Called by hosts (the popover re-collapses on every open).
    reset() {
      this.showAll = false;
    },
    resolveActor(email) {
      if (!email) return null;
      return this.$store.getters['goals/resolveOwner'](email);
    },
    actorName(item) {
      if (item.isAutomation) {
        const source = this.keyResult.metric_source;
        if (source && source.toolkit_slug) return toolkitDisplayName(source.toolkit_slug);
        return this.$t('performance.goals.kr_history_automation_actor');
      }
      const actor = this.resolveActor(item.updated_by);
      if (actor && actor.fullname) return actor.fullname;
      if (item.updated_by) return item.updated_by.split('@')[0];
      return this.$t('performance.goals.kr_history_unknown_actor');
    },
    actorInitials(item) {
      const name = this.actorName(item);
      if (!name) return '?';
      const parts = String(name).trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return (parts[0][0] || '?').toUpperCase();
    },
    avatarPic(item) {
      const actor = this.resolveActor(item.updated_by);
      return (actor && actor.large_pic) || '';
    },
    avatarStyle(item) {
      const actor = this.resolveActor(item.updated_by);
      const pic = this.avatarPic(item);
      return {
        backgroundColor: (actor && actor.house_color) || '#ddd',
        backgroundImage: pic ? 'url(' + pic + ')' : 'none',
      };
    },
    statusChipStyle(status) {
      const color = KR_STATUS_COLORS[status] || '#909399';
      return { backgroundColor: color + '26', color };
    },
    statusLabel(status) {
      return KR_STATUS_LABELS[status] || status;
    },
    relTime(value) {
      return formatRelativeKrTime(value);
    },
    formatValue(value) {
      return formatKrValue(value, this.unit);
    },
    formatDelta(delta) {
      const sign = delta > 0 ? '+' : '-';
      return sign + formatTargetNumber(Math.abs(delta));
    },
    // The slice owner's personal target ("2" in "2 / 2"). Null when the
    // person is no longer an owner — the row then shows just their value.
    ownerTarget(item) {
      const target = this.ownerTargetMap[item.ownerEmail];
      return typeof target === 'number' && target > 0 ? target : null;
    },
  },
};
</script>

<style scoped>
.krtl__empty {
  padding: 14px;
  font-size: 12px;
  color: #909399;
  text-align: center;
}

.krtl__list {
  list-style: none;
  margin: 0;
  padding: 0 10px 4px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Row vocabulary borrowed from the automation Activity feed: actor mark,
   head line, detail line. Status is carried by the value-line chip. */
.krtl-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 8px 10px;
  background: #ffffff;
  border: 1px solid #eef0f5;
  border-radius: 10px;
  transition: border-color 160ms ease;
}

.krtl-row:hover {
  border-color: #e2e4ea;
}

.krtl-row__mark {
  flex-shrink: 0;
  margin-top: 1px;
}

.krtl-row__avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: white;
}

.krtl-row__zap {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #f2ecfb;
  color: #6c30cc;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.krtl-row__main {
  flex: 1;
  min-width: 0;
}

.krtl-row__head {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.krtl-row__actor {
  font-size: 12px;
  font-weight: 700;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.krtl-row__actor--automation {
  color: #6c30cc;
}

.krtl-row__time {
  margin-left: auto;
  font-size: 11px;
  font-weight: 500;
  color: #9a9a9a;
  white-space: nowrap;
  flex-shrink: 0;
}

.krtl-row__value-line {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
}

.krtl-row__value {
  font-size: 12px;
  font-weight: 700;
  color: #4a4a4a;
  white-space: nowrap;
}

.krtl-row__delta {
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.krtl-row__delta--up {
  color: #1f8b5a;
}

.krtl-row__delta--down {
  color: #b22a2a;
}

/* Labeled rollup tag — explicit so the team number can't read as a
   personal value on individual-mode rows. */
.krtl-row__team-total {
  font-size: 10px;
  font-weight: 700;
  color: #8a8a8a;
  background: #f5f5fa;
  border-radius: 999px;
  padding: 1px 7px;
  white-space: nowrap;
  letter-spacing: 0.02em;
}

/* Legacy rows (no recorded slice) lead with the labeled rollup at full
   value-line weight so the row still has a primary number. */
.krtl-row__team-total--lead {
  font-size: 11px;
  color: #626262;
}

.krtl-row__status {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  white-space: nowrap;
  letter-spacing: 0.03em;
}

.krtl-row__note {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #626262;
  line-height: 1.45;
  word-break: break-word;
}

.krtl__show-all {
  display: block;
  width: calc(100% - 20px);
  margin: 6px 10px 8px;
  padding: 6px 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  color: #6c30cc;
  background: #f2ecfb;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 160ms ease;
}

.krtl__show-all:hover {
  background: #e6dafa;
}
</style>
